import React, { useState, useEffect } from 'react';
import { 
  FiPlus,
  FiX,
  FiSearch,
  FiShoppingCart,
  FiCheck,
  FiAlertCircle
} from 'react-icons/fi';
import axios from 'axios';

const CreateOrderModal = ({ isOpen, onClose, onOrderCreated }) => {
  const [step, setStep] = useState(1); // 1: Search student, 2: Select course, 3: Payment
  const [phone, setPhone] = useState('');
  const [student, setStudent] = useState(null);
  const [courses, setCourses] = useState([]);
  const [selectedCourse, setSelectedCourse] = useState(null);
  const [orderId, setOrderId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [paymentMethod, setPaymentMethod] = useState('cash');
  const [amountPaid, setAmountPaid] = useState('');
  const [success, setSuccess] = useState(false);
  const [existingCart, setExistingCart] = useState(null);
  const [cartItems, setCartItems] = useState([]);
  const [totalAmount, setTotalAmount] = useState(0);
  const [contentAlreadyInCart, setContentAlreadyInCart] = useState(false);

  // Fetch courses when modal opens
  useEffect(() => {
    if (isOpen) {
      fetchCourses();
    } else {
      // Reset state when modal closes
      resetForm();
    }
  }, [isOpen]);

  const resetForm = () => {
    setStep(1);
    setPhone('');
    setStudent(null);
    setSelectedCourse(null);
    setOrderId(null);
    setError(null);
    setPaymentMethod('cash');
    setAmountPaid('');
    setSuccess(false);
    setExistingCart(null);
    setCartItems([]);
    setTotalAmount(0);
    setContentAlreadyInCart(false);
  };

  const fetchCourses = async () => {
    try {
      setLoading(true);
      const response = await axios.get('https://apissc.sainikschoolcadet.com/api/contents/');
      setCourses(response.data);
      setLoading(false);
    } catch (err) {
      setError('Failed to fetch courses');
      setLoading(false);
    }
  };

  const searchStudent = async () => {
    if (!phone || phone.length < 10) {
      setError('Please enter a valid phone number');
      return;
    }

    try {
      setLoading(true);
      setError(null);
      const response = await axios.get(`https://apissc.sainikschoolcadet.com/api/students/getbyphone/${phone}`);
      setStudent(response.data);
      setLoading(false);
      setStep(2);
    } catch (err) {
      setError('Student not found. Please check the phone number.');
      setLoading(false);
    }
  };

  // Check student's existing cart
  const checkStudentCart = async () => {
    if (!student || !student.s_id) return;
    
    try {
      const response = await axios.get(`https://apissc.sainikschoolcadet.com/api/StudentOrders/student/${student.s_id}/cart`);
      
      if (response.data && response.data.length > 0) {
        // Process the cart data
        const cartItems = [];
        let totalAmount = 0;
        let foundOrderId = null;
        
        // Get details for each cart item
        for (const order of response.data) {
          foundOrderId = foundOrderId || order.order_id;
          
          try {
            // Get content details for each item
            const contentResponse = await axios.get(`https://apissc.sainikschoolcadet.com/api/contents/${order.content_id}`);
            const content = contentResponse.data;
            
            // Check if the selected course is already in cart
            if (selectedCourse && order.content_id === selectedCourse.content_id) {
              setContentAlreadyInCart(true);
            }
            
            cartItems.push({
              order_id: order.order_id,
              content_id: order.content_id,
              title: content.title,
              price: content.sell_price,
              type: content.content_type
            });
            
            totalAmount += parseFloat(content.sell_price || 0);
          } catch (error) {
            console.error("Error fetching content details:", error);
            cartItems.push({
              order_id: order.order_id,
              content_id: order.content_id,
              title: `Course #${order.content_id}`,
              price: 0,
              type: 'Unknown'
            });
          }
        }
        
        // Set state with cart information
        setExistingCart(response.data[0]);
        setOrderId(foundOrderId);
        setCartItems(cartItems);
        setTotalAmount(totalAmount);
        
        return true;
      }
      
      return false;
    } catch (err) {
      console.error("Error checking cart:", err);
      return false;
    }
  };

  const selectCourse = (course) => {
    setSelectedCourse(course);
    setAmountPaid(course.sell_price.toString()); // Default to list price
  };

  const addToCart = async () => {
    if (!student || !selectedCourse) {
      setError('Please select a student and course');
      return;
    }

    try {
      setLoading(true);
      setError(null);
      
      // First check if this student already has items in cart
      const hasExistingCart = await checkStudentCart();
      
      // If selected content is already in cart, show error
      if (contentAlreadyInCart) {
        setError('This course is already in the student\'s cart');
        setLoading(false);
        return;
      }
      
      // If student has other items in cart, we'll skip the API call and go directly to payment
      if (hasExistingCart) {
        // Update the amount to include the new course
        const newTotal = totalAmount + parseFloat(selectedCourse.sell_price);
        setTotalAmount(newTotal);
        setAmountPaid(newTotal.toString());
        
        // Add the selected course to the cart items (frontend only, API call will happen when completing)
        setCartItems([...cartItems, {
          content_id: selectedCourse.content_id,
          title: selectedCourse.title,
          price: selectedCourse.sell_price,
          type: selectedCourse.content_type,
          isNew: true // Mark as new item
        }]);
        
        setLoading(false);
        setStep(3);
        return;
      }
      
      // If no existing cart, create a new cart item
      const cartData = {
        student_id: student.s_id,
        content_id: selectedCourse.content_id
      };

      const response = await axios.post('https://apissc.sainikschoolcadet.com/api/StudentOrders/add-to-cart', cartData);
      setOrderId(response.data.order.order_id);
      
      // Set cart items with just this new item
      setCartItems([{
        order_id: response.data.order.order_id,
        content_id: selectedCourse.content_id, 
        title: selectedCourse.title,
        price: selectedCourse.sell_price,
        type: selectedCourse.content_type
      }]);
      
      setTotalAmount(parseFloat(selectedCourse.sell_price));
      setLoading(false);
      setStep(3);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to add item to cart');
      setLoading(false);
    }
  };

  const completeOrder = async () => {
    if (!orderId && !existingCart) {
      setError('No order found to complete');
      return;
    }
    
    if (!amountPaid || parseFloat(amountPaid) <= 0) {
      setError('Please enter a valid amount');
      return;
    }

    try {
      setLoading(true);
      setError(null);
      
      // If we have a selectedCourse but it's not in the existing cart items, add it first
      if (selectedCourse && existingCart && !contentAlreadyInCart) {
        const cartData = {
          student_id: student.s_id,
          content_id: selectedCourse.content_id
        };
        
        await axios.post('https://apissc.sainikschoolcadet.com/api/StudentOrders/add-to-cart', cartData);
        // The orderId stays the same as the existing cart's order_id
      }
      
      const paymentData = {
        amount_paid: parseFloat(amountPaid),
        payment_method: paymentMethod
      };

      // Use the existing cart's order ID or the new order's ID
      const activeOrderId = orderId || (existingCart && existingCart.order_id);
      
      await axios.put(`https://apissc.sainikschoolcadet.com/api/StudentOrders/complete/${activeOrderId}`, paymentData);
      setLoading(false);
      setSuccess(true);
      
      // Notify parent component for data refresh
      if (onOrderCreated) {
        onOrderCreated();
      }
      
      // Auto close after success
      setTimeout(() => {
        onClose();
      }, 2000);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to complete the order');
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl">
        {/* Header */}
        <div className="flex items-center justify-between border-b px-6 py-4">
          <h2 className="text-xl font-semibold text-gray-800">Create Order</h2>
          <button 
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700 focus:outline-none"
          >
            <FiX size={24} />
          </button>
        </div>
        
        {/* Progress Steps */}
        <div className="px-6 pt-4">
          <div className="flex items-center justify-between">
            <div className="flex flex-col items-center">
              <div className={`w-8 h-8 flex items-center justify-center rounded-full ${step >= 1 ? 'bg-blue-500 text-white' : 'bg-gray-200'}`}>
                1
              </div>
              <span className="text-xs mt-1">Find Student</span>
            </div>
            <div className="flex-1 h-1 mx-2 bg-gray-200">
              <div className={`h-1 ${step >= 2 ? 'bg-blue-500' : 'bg-gray-200'}`} style={{width: step >= 2 ? '100%' : '0%'}}></div>
            </div>
            <div className="flex flex-col items-center">
              <div className={`w-8 h-8 flex items-center justify-center rounded-full ${step >= 2 ? 'bg-blue-500 text-white' : 'bg-gray-200'}`}>
                2
              </div>
              <span className="text-xs mt-1">Select Course</span>
            </div>
            <div className="flex-1 h-1 mx-2 bg-gray-200">
              <div className={`h-1 ${step >= 3 ? 'bg-blue-500' : 'bg-gray-200'}`} style={{width: step >= 3 ? '100%' : '0%'}}></div>
            </div>
            <div className="flex flex-col items-center">
              <div className={`w-8 h-8 flex items-center justify-center rounded-full ${step >= 3 ? 'bg-blue-500 text-white' : 'bg-gray-200'}`}>
                3
              </div>
              <span className="text-xs mt-1">Payment</span>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="px-6 py-4">
          {/* Error Display */}
          {error && (
            <div className="mb-4 p-3 bg-red-100 border border-red-300 text-red-700 rounded">
              {error}
            </div>
          )}
          
          {/* Success Message */}
          {success && (
            <div className="mb-4 p-3 bg-green-100 border border-green-300 text-green-700 rounded flex items-center">
              <FiCheck className="mr-2" /> Order created successfully!
            </div>
          )}

          {/* Step 1: Search Student */}
          {step === 1 && (
            <div>
              <label className="block text-gray-700 text-sm font-bold mb-2">
                Search Student by Phone Number
              </label>
              <div className="flex">
                <input
                  type="text"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  placeholder="Enter phone number (10 digits)"
                  className="shadow appearance-none border rounded-l py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline flex-grow"
                  maxLength="10"
                />
                <button
                  onClick={searchStudent}
                  disabled={loading}
                  className="bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded-r focus:outline-none focus:shadow-outline flex items-center"
                >
                  {loading ? 'Searching...' : <><FiSearch className="mr-2" /> Search</>}
                </button>
              </div>
            </div>
          )}

          {/* Step 2: Select Course */}
          {step === 2 && (
            <div>
              <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded">
                <h3 className="font-semibold text-gray-800">Student Details</h3>
                <div className="grid grid-cols-2 gap-2 mt-2">
                  <div>
                    <p className="text-sm text-gray-600">Name</p>
                    <p className="font-medium">{student.f_name}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Phone</p>
                    <p className="font-medium">{student.phonenumber}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Class Entry</p>
                    <p className="font-medium">{student.c_entry}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">School</p>
                    <p className="font-medium">{student.c_school}</p>
                  </div>
                </div>
              </div>

              <h3 className="font-semibold text-gray-800 mb-2">Select a Course</h3>
              <div className="max-h-64 overflow-y-auto border rounded">
                {courses.map(course => (
                  <div 
                    key={course.content_id}
                    onClick={() => selectCourse(course)}
                    className={`p-3 border-b hover:bg-gray-50 cursor-pointer flex justify-between items-center ${selectedCourse?.content_id === course.content_id ? 'bg-blue-50' : ''}`}
                  >
                    <div>
                      <h4 className="font-medium">{course.title}</h4>
                      <p className="text-sm text-gray-600">{course.content_type}</p>
                    </div>
                    <div className="text-right">
                      <p className="font-bold text-blue-600">{new Intl.NumberFormat('en-IN', {
                        style: 'currency',
                        currency: 'INR'
                      }).format(course.sell_price)}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Step 3: Payment */}
          {step === 3 && (
            <div>
              <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded">
                <h3 className="font-semibold text-gray-800">Order Summary</h3>
                <div className="grid grid-cols-2 gap-2 mt-2">
                  <div>
                    <p className="text-sm text-gray-600">Student</p>
                    <p className="font-medium">{student.f_name}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Order ID</p>
                    <p className="font-medium">#{orderId || (existingCart && existingCart.order_id) || 'New Order'}</p>
                  </div>
                  <div className="col-span-2">
                    <p className="text-sm text-gray-600 mb-1">Items in Cart</p>
                    <div className="border rounded divide-y">
                      {cartItems.map((item, index) => (
                        <div key={index} className="p-2 flex justify-between">
                          <span className={item.isNew ? 'font-semibold text-blue-600' : ''}>
                            {item.title || `Course #${item.content_id}`}
                            {item.isNew && ' (New)'}
                          </span>
                          <span className="font-medium">
                            {new Intl.NumberFormat('en-IN', {
                              style: 'currency',
                              currency: 'INR'
                            }).format(item.price || 0)}
                          </span>
                        </div>
                      ))}
                      <div className="p-2 flex justify-between bg-gray-50">
                        <span className="font-medium">Total</span>
                        <span className="font-bold">
                          {new Intl.NumberFormat('en-IN', {
                            style: 'currency',
                            currency: 'INR'
                          }).format(totalAmount)}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="mb-4">
                <label className="block text-gray-700 text-sm font-bold mb-2">
                  Payment Method
                </label>
                <select
                  value={paymentMethod}
                  onChange={(e) => setPaymentMethod(e.target.value)}
                  className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                >
                  <option value="cash">Cash</option>
                  <option value="upi">UPI</option>
                  <option value="razorpay_online">Razorpay Online</option>
                </select>
              </div>

              <div className="mb-4">
                <label className="block text-gray-700 text-sm font-bold mb-2">
                  Amount Paid
                </label>
                <input
                  type="number"
                  value={amountPaid}
                  onChange={(e) => setAmountPaid(e.target.value)}
                  className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                />
              </div>
            </div>
          )}
        </div>
        
        {/* Footer */}
        <div className="px-6 py-4 bg-gray-50 rounded-b-lg flex justify-between">
          {step > 1 && (
            <button
              onClick={() => setStep(step - 1)}
              className="px-4 py-2 border border-gray-300 rounded text-gray-700 hover:bg-gray-100"
            >
              Back
            </button>
          )}
          <div className="ml-auto">
            {step === 1 && (
              <button
                onClick={searchStudent}
                disabled={loading}
                className="bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded focus:outline-none focus:shadow-outline"
              >
                {loading ? 'Searching...' : 'Search'}
              </button>
            )}
            {step === 2 && (
              <button
                onClick={addToCart}
                disabled={loading || !selectedCourse}
                className="bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded focus:outline-none focus:shadow-outline flex items-center"
              >
                {loading ? 'Processing...' : <><FiShoppingCart className="mr-2" /> Add to Cart</>}
              </button>
            )}
            {step === 3 && (
              <button
                onClick={completeOrder}
                disabled={loading || success}
                className="bg-green-500 hover:bg-green-700 text-white font-bold py-2 px-4 rounded focus:outline-none focus:shadow-outline flex items-center"
              >
                {loading ? 'Processing...' : <><FiCheck className="mr-2" /> Complete Order</>}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

// Add this button component to the Orders Dashboard
const CreateOrderButton = ({ onOrderCreated }) => {
  const [modalOpen, setModalOpen] = useState(false);

  return (
    <>
      <button 
        onClick={() => setModalOpen(true)}
        className="flex items-center bg-blue-600 hover:bg-blue-700 text-white font-medium py-2 px-4 rounded-lg"
      >
        <FiPlus className="mr-2" /> Create Order
      </button>
      
      <CreateOrderModal 
        isOpen={modalOpen} 
        onClose={() => setModalOpen(false)} 
        onOrderCreated={onOrderCreated}
      />
    </>
  );
};

export { CreateOrderButton, CreateOrderModal };